/**
 * AI Assistant Store
 *
 * MobX store for the AI Assistant drawer component.
 * Manages conversations, messages, and search state.
 * LLM provider selection is handled automatically server-side.
 */

import { makeAutoObservable, runInAction, flow } from 'mobx';
import { RootStore } from '../../store/RootStore';
import { getApi } from '../../api';
import { tokenManager } from '../../api/tokenManager';
import { SortOrder, EvaluationRating } from '../../api/graphql';
import type { QueryExplanation } from './useAiSearchStream';
import { graphqlToDate } from '../utils';

/**
 * Data for saving an evaluation (rating feedback)
 */
export interface EvaluationData {
  /** Response ID to update */
  responseId: string;
  /** Rating: ThumbsUp or ThumbsDown */
  rating: EvaluationRating;
}

/**
 * A message in the conversation
 */
export interface AiAssistantMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
  sources?: Array<{
    type: string;
    id: string;
    title: string;
    snippet?: string;
  }>;
  /** Response ID for feedback tracking (only for assistant messages) */
  responseId?: string;
}

/**
 * A conversation summary for the history list
 */
export interface ConversationSummary {
  id: string;
  startedAt: Date;
  lastMessageAt: Date;
  messageCount: number;
  preview: string;
}

/**
 * Group conversations by time period
 */
export interface ConversationGroup {
  label: string;
  conversations: ConversationSummary[];
}

export class AiAssistantStore {
  // UI State
  isOpen: boolean = false;
  isLoading: boolean = false;
  isLoadingHistory: boolean = false;
  error: string | null = null;
  evaluationEnabled: boolean = true;

  // Streaming state
  isStreaming: boolean = false;
  streamingContent: string = '';
  streamingToolStatus: { name: string; status: 'running' | 'done'; count?: number; elapsedMs?: number } | null = null;
  lastExplanation: QueryExplanation | null = null;

  // Current conversation
  currentConversationId: string | null = null;
  messages: AiAssistantMessage[] = [];

  // History
  conversations: ConversationSummary[] = [];
  historyLoaded: boolean = false;

  constructor(private rootStore: RootStore) {
    makeAutoObservable(this, {
      loadConversation: flow,
      loadHistory: flow,
      deleteConversation: flow,
      // saveEvaluation is now async (not flow) since it uses fetch directly
    });
  }

  /**
   * Open the assistant drawer
   */
  open(): void {
    this.isOpen = true;
    if (!this.historyLoaded) {
      this.loadHistory();
    }
  }

  /**
   * Close the assistant drawer
   */
  close(): void {
    this.isOpen = false;
  }

  /**
   * Toggle evaluation mode
   */
  toggleEvaluation(): void {
    this.evaluationEnabled = !this.evaluationEnabled;
  }

  /**
   * Toggle the assistant drawer
   */
  toggle(): void {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  /**
   * Start a new conversation
   */
  newConversation(): void {
    runInAction(() => {
      this.currentConversationId = null;
      this.messages = [];
      this.error = null;
    });
  }

  /**
   * Check if there are messages to export
   */
  get hasMessages(): boolean {
    return this.messages.length > 0;
  }

  /**
   * Get conversations grouped by time period
   */
  get groupedConversations(): ConversationGroup[] {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const thisWeek = new Date(today);
    thisWeek.setDate(thisWeek.getDate() - 7);

    const groups: ConversationGroup[] = [
      { label: 'Hoy', conversations: [] },
      { label: 'Ayer', conversations: [] },
      { label: 'Esta semana', conversations: [] },
      { label: 'Anteriores', conversations: [] },
    ];

    for (const conv of this.conversations) {
      const date = new Date(conv.lastMessageAt);
      if (date >= today) {
        groups[0].conversations.push(conv);
      } else if (date >= yesterday) {
        groups[1].conversations.push(conv);
      } else if (date >= thisWeek) {
        groups[2].conversations.push(conv);
      } else {
        groups[3].conversations.push(conv);
      }
    }

    // Filter out empty groups
    return groups.filter((g) => g.conversations.length > 0);
  }

  /**
   * Load a conversation from history
   */
  *loadConversation(conversationId: string): Generator<any, void, any> {
    runInAction(() => {
      this.isLoading = true;
      this.error = null;
    });

    try {
      const api = yield* getApi(
        this.rootStore.authStore,
        this.rootStore.activityStore
      );

      const result = yield api.aiSearchConversationOne({
        id: conversationId,
      });

      if (result.aiSearchConversationOne) {
        const conv = result.aiSearchConversationOne;
        const messages = (conv.messages as AiAssistantMessage[]) || [];

        runInAction(() => {
          this.currentConversationId = conv.id;
          this.messages = messages;
          this.isLoading = false;
        });
      }
    } catch (error) {
      runInAction(() => {
        this.isLoading = false;
        this.error =
          error instanceof Error ? error.message : 'Error al cargar la conversación';
      });
      console.error('[AiAssistantStore] Error loading conversation:', error);
    }
  }

  /**
   * Load conversation history
   */
  *loadHistory(): Generator<any, void, any> {
    runInAction(() => {
      this.isLoadingHistory = true;
    });

    try {
      const api = yield* getApi(
        this.rootStore.authStore,
        this.rootStore.activityStore
      );

      const result = yield api.aiSearchConversationListWithOffset({
        where: {},
        orderBy: [{ lastMessageAt: SortOrder.Desc }],
        offset: 0,
        take: 50,
      });

      if (result.aiSearchConversationListWithOffset) {
        const summaries: ConversationSummary[] =
          result.aiSearchConversationListWithOffset.map((conv: any) => {
            const messages = (conv.messages as AiAssistantMessage[]) || [];
            const firstUserMessage = messages.find((m) => m.role === 'user');
            const preview = firstUserMessage?.content.substring(0, 50) || 'Conversación vacía';

            return {
              id: conv.id,
              startedAt: new Date(conv.startedAt),
              lastMessageAt: new Date(conv.lastMessageAt),
              messageCount: conv.messageCount,
              preview: preview.length >= 50 ? preview + '...' : preview,
            };
          });

        runInAction(() => {
          this.conversations = summaries;
          this.historyLoaded = true;
          this.isLoadingHistory = false;
        });
      }
    } catch (error) {
      runInAction(() => {
        this.isLoadingHistory = false;
      });
      console.error('[AiAssistantStore] Error loading history:', error);
    }
  }

  /**
   * Delete a conversation
   */
  *deleteConversation(conversationId: string): Generator<any, void, any> {
    try {
      const api = yield* getApi(
        this.rootStore.authStore,
        this.rootStore.activityStore
      );

      yield api.aiSearchConversationDelete({
        id: conversationId,
      });

      runInAction(() => {
        this.conversations = this.conversations.filter((c) => c.id !== conversationId);

        // If we deleted the current conversation, start a new one
        if (this.currentConversationId === conversationId) {
          this.currentConversationId = null;
          this.messages = [];
        }
      });
    } catch (error) {
      console.error('[AiAssistantStore] Error deleting conversation:', error);
      throw error;
    }
  }

  /**
   * Export current conversation to Markdown
   */
  exportToMarkdown(): void {
    if (this.messages.length === 0) return;

    const now = new Date();
    const dateStr = now.toISOString().split('T')[0];
    const timeStr = now.toTimeString().split(' ')[0].replace(/:/g, '-');

    let markdown = `# Conversación AI Search\n\n`;
    markdown += `**Fecha**: ${now.toLocaleDateString('es-ES', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })}\n\n`;
    markdown += `---\n\n`;

    for (const message of this.messages) {
      const role = message.role === 'user' ? 'Usuario' : 'Asistente';
      markdown += `## ${role}\n\n`;
      markdown += `${message.content}\n\n`;

      if (message.sources && message.sources.length > 0) {
        markdown += `**Fuentes:**\n`;
        for (const source of message.sources) {
          markdown += `- ${source.title} (${source.type})\n`;
        }
        markdown += `\n`;
      }
    }

    // Create and download file
    const blob = new Blob([markdown], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `ai-search-${dateStr}-${timeStr}.md`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  // =========================================
  // Streaming Methods
  // =========================================

  /**
   * Start streaming a message
   * Called when user submits a query
   */
  startStreaming(query: string): void {
    const userMessage: AiAssistantMessage = {
      role: 'user',
      content: query,
      timestamp: new Date().toISOString(),
    };

    runInAction(() => {
      this.messages.push(userMessage);
      this.isStreaming = true;
      this.streamingContent = '';
      this.streamingToolStatus = null;
      this.lastExplanation = null;
      this.error = null;
    });
  }

  /**
   * Update streaming content with new delta
   */
  updateStreamingContent(delta: string): void {
    runInAction(() => {
      this.streamingContent += delta;
      this.streamingToolStatus = null; // Clear tool status when text arrives
    });
  }

  /**
   * Update tool execution status
   */
  updateToolStatus(status: { name: string; status: 'running' | 'done'; count?: number; elapsedMs?: number } | null): void {
    runInAction(() => {
      this.streamingToolStatus = status;
    });
  }

  /**
   * Set query explanation
   */
  setExplanation(explanation: QueryExplanation | null): void {
    runInAction(() => {
      this.lastExplanation = explanation;
    });
  }

  /**
   * Finish streaming and add assistant message
   */
  finishStreaming(
    conversationId: string,
    sources: AiAssistantMessage['sources'],
    responseId?: string | null
  ): void {
    const wasNewConversation = !this.currentConversationId;

    const assistantMessage: AiAssistantMessage = {
      role: 'assistant',
      content: this.streamingContent,
      timestamp: new Date().toISOString(),
      sources,
      responseId: responseId || undefined,
    };

    runInAction(() => {
      this.messages.push(assistantMessage);
      this.currentConversationId = conversationId;
      this.isStreaming = false;
      this.streamingContent = '';
      this.streamingToolStatus = null;
    });

    // Refresh history if this was a new conversation
    if (wasNewConversation) {
      this.loadHistory();
    }
  }

  /**
   * Handle streaming error
   */
  handleStreamingError(errorMessage: string): void {
    runInAction(() => {
      this.isStreaming = false;
      this.streamingContent = '';
      this.streamingToolStatus = null;
      this.error = errorMessage;
    });
  }

  /**
   * Cancel streaming and clean up state
   * Called when user presses cancel button
   */
  cancelStreaming(): void {
    runInAction(() => {
      this.isStreaming = false;
      this.streamingContent = '';
      this.streamingToolStatus = null;
      // Don't set error - cancellation is user-initiated
    });
  }

  /**
   * Get current streaming or loading state
   */
  get isBusy(): boolean {
    return this.isLoading || this.isStreaming;
  }

  /**
   * Format relative time
   */
  formatRelativeTime(date: Date): string {
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'ahora';
    if (minutes < 60) return `hace ${minutes}m`;
    if (hours < 24) return `hace ${hours}h`;
    if (days === 1) return 'ayer';
    if (days < 7) return `hace ${days}d`;
    return graphqlToDate(date);
  }

  // =========================================
  // Evaluation Methods
  // =========================================

  /**
   * Save a rating for a response via REST endpoint
   * Returns true on success, throws on error
   */
  async saveEvaluation(data: EvaluationData): Promise<boolean> {
    try {
      const token = await tokenManager.getValidAccessToken();
      if (!token) {
        throw new Error('No valid token available');
      }

      const response = await fetch(`/api/ai-search/response/${data.responseId}/rating`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ rating: data.rating }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      return true;
    } catch (error) {
      console.error('[AiAssistantStore] Error saving evaluation:', error);
      throw error;
    }
  }

  /**
   * Dispose of the store
   */
  dispose(): void {
    // Cleanup if needed
  }
}
